import React, { useEffect, useRef, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { normalizeTimestamp } from "../../utils/dateUtils";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

function RealtimeActiveChart() {
  const [data, setData] = useState([]);

  // Guarda o último valor de "active" calculado pelo snapshot
  const activeRef = useRef(0);

  // Controle do timer
  const timerRef = useRef(null);

  // =========================================================
  // Helper: adiciona um ponto no gráfico (mantém últimos 20)
  // =========================================================
  const pushPoint = (activeValue) => {
    setData((prev) => {
      const next = [
        ...prev.slice(-19),
        {
          time: new Date().toLocaleTimeString("pt-BR", {
            hour12: false,
          }),
          active: activeValue,
        },
      ];

      // Garantia: se tiver só 1 ponto ainda, duplica para a linha aparecer bem
      if (next.length === 1) {
        return [
          {
            time: new Date(Date.now() - 1000).toLocaleTimeString("pt-BR", {
              hour12: false,
            }),
            active: activeValue,
          },
          next[0],
        ];
      }

      return next;
    });
  };

  useEffect(() => {
    // =========================================================
    // 1) Snapshot: recalcula "active" sempre que Firestore muda
    // =========================================================
    const unsub = onSnapshot(collection(db, "users"), (snap) => {
      let active = 0;
      const now = Date.now();

      snap.forEach((doc) => {
        const u = doc.data();
        const lastActive = normalizeTimestamp(u.lastActiveAt);
        if (!lastActive) return;

        const diffMinutes = (now - lastActive.getTime()) / (1000 * 60);

        // ✅ Considera ativo nos últimos 5 minutos
        if (diffMinutes <= 5) active++;
      });

      activeRef.current = active;

      // Atualiza o gráfico imediatamente quando houver mudança real
      pushPoint(active);
    });

    // =========================================================
    // 2) Timer: mesmo sem mudanças no Firestore, cria pontos
    //    (senão não tem linha / não "anda" no tempo real)
    // =========================================================
    timerRef.current = setInterval(() => {
      pushPoint(activeRef.current);
    }, 8000); // 8s (pode ajustar para 5s ou 10s)

    // Seed inicial para não começar vazio
    pushPoint(0);

    return () => {
      try {
        unsub();
      } catch (_) {}

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="chartBox">
      <h3>Usuários ativos agora (tempo real) ⚡</h3>

      <div className="chart-container" style={{ height: 260 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={data}
            margin={{ top: 10, right: 10, left: 0, bottom: 10 }}
          >
            <CartesianGrid stroke="#ffe5ef" strokeDasharray="3 3" />

            <XAxis
              dataKey="time"
              tick={{ fontSize: 11, fill: "#666" }}
              interval="preserveStartEnd"
            />

            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#666" }} />

            <Tooltip
              formatter={(value) => [`${value}`, "Ativos"]}
              labelFormatter={(label) => `Hora: ${label}`}
              contentStyle={{
                backgroundColor: "white",
                border: "1px solid #ff4f9a",
                borderRadius: "8px",
                fontSize: "12px",
                padding: "8px",
              }}
            />

            <Area
              type="monotone"
              dataKey="active"
              stroke="#ff4f9a"
              strokeWidth={2.5}
              fill="#ffd6ea"
              dot={{ r: 3, stroke: "#ff4f9a", strokeWidth: 2, fill: "#fff" }}
              activeDot={{ r: 6 }}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default RealtimeActiveChart;
